import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';

// Revenue Chart Data
export interface RevenueChartData {
  month: string;
  revenue: number;
}

export const useRevenueChart = (year?: number) => {
  const yearParam = year ? `?year=${year}` : '';
  const { data, error, isLoading, mutate } = useSWR<RevenueChartData[]>(
    `/dashboard/revenue-chart${yearParam}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

// Expense Category Breakdown
export interface ExpenseCategoryData {
  category: string;
  amount: number;
  count: number;
}

export const useExpenseCategoryBreakdown = (year?: number) => {
  const yearParam = year ? `?year=${year}` : '';
  const { data, error, isLoading, mutate } = useSWR<ExpenseCategoryData[]>(
    `/dashboard/expense-category-breakdown${yearParam}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

// Revenue vs Expense
export interface RevenueVsExpenseData {
  month: string;
  revenue: number;
  expense: number;
}

export const useRevenueVsExpense = (year?: number) => {
  const yearParam = year ? `?year=${year}` : '';
  const { data, error, isLoading, mutate } = useSWR<RevenueVsExpenseData[]>(
    `/dashboard/revenue-vs-expense${yearParam}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

// Monthly Trends
export interface MonthlyTrendData {
  month: string;
  revenue: number;
  expenses: number;
  clients: number;
  tasks: number;
}

export const useMonthlyTrends = (year?: number) => {
  const yearParam = year ? `?year=${year}` : '';
  const { data, error, isLoading, mutate } = useSWR<MonthlyTrendData[]>(
    `/dashboard/monthly-trends${yearParam}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

// Invoice Status Breakdown
export interface InvoiceStatusData {
  status: string;
  count: number;
  amount: number;
  color?: string;
}

export const useInvoiceStatusBreakdown = () => {
  const { data, error, isLoading, mutate } = useSWR<InvoiceStatusData[]>(
    '/dashboard/invoice-status-breakdown',
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

// Client Growth
export interface ClientGrowthData {
  month: string;
  new: number;
  total: number;
}

export const useClientGrowth = () => {
  const { data, error, isLoading, mutate } = useSWR<ClientGrowthData[]>(
    '/dashboard/client-growth',
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

// Task Priority Distribution
export interface TaskPriorityData {
  priority: string;
  count: number;
}

export const useTaskPriorityDistribution = () => {
  const { data, error, isLoading, mutate } = useSWR<TaskPriorityData[]>(
    '/dashboard/task-priority-distribution',
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

// Activity Heatmap
export interface ActivityHeatmapData {
  date: string;
  count: number;
}

export const useActivityHeatmap = () => {
  const { data, error, isLoading, mutate } = useSWR<ActivityHeatmapData[]>(
    '/dashboard/activity-heatmap',
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

// Task Completion Rate
export interface TaskCompletionRateData {
  total: number;
  completed: number;
  inProgress: number;
  pending: number;
  overdue: number;
  completionRate: number;
  byStatus: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
}

export const useTaskCompletionRate = (startDate?: string, endDate?: string) => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  
  const queryString = params.toString();
  const { data, error, isLoading, mutate } = useSWR<TaskCompletionRateData>(
    `/dashboard/task-completion-rate${queryString ? `?${queryString}` : ''}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data,
    isLoading,
    isError: error,
    mutate,
  };
};

// Upcoming Deadlines
export interface UpcomingDeadline {
  id: number;
  type: 'task' | 'appointment' | 'invoice';
  title: string;
  dueDate: string;
  entityId: number;
  priority?: string;
  status?: string;
  link: string;
}

export const useUpcomingDeadlines = (days: number = 30) => {
  const { data, error, isLoading, mutate } = useSWR<UpcomingDeadline[]>(
    `/dashboard/upcoming-deadlines?days=${days}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: 300000, // Refresh every 5 minutes
    }
  );

  return {
    data: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

// Revenue Trends (enhanced with multiple periods)
export interface RevenueTrendsData {
  period: string;
  revenue: number;
  year?: number;
}

export const useRevenueTrends = (
  period: 'monthly' | 'quarterly' | 'yearly' = 'monthly',
  years?: number[]
) => {
  const params = new URLSearchParams();
  params.append('period', period);
  if (years && years.length > 0) {
    params.append('years', years.join(','));
  }
  
  const { data, error, isLoading, mutate } = useSWR<Record<string, RevenueTrendsData[]>>(
    `/dashboard/revenue-trends?${params.toString()}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || {},
    isLoading,
    isError: error,
    mutate,
  };
};













